import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import UploadView from "./components/UploadView";
import LoadingView from "./components/LoadingView";
import Dashboard from "./components/Dashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const [view, setView] = useState("upload"); // upload, loading, dashboard
  const [progress, setProgress] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  const { resultId } = useParams();
  const navigate = useNavigate();

  // Load shared result if URL has resultId
  useEffect(() => {
    if (resultId) {
      loadSharedResult(resultId);
    }
  }, [resultId]);

  const loadSharedResult = async (id) => {
    setView("loading");
    setProgress(50);
    const started = Date.now();
    const maxWaitMs = 5 * 60 * 1000;
    try {
      while (true) {
        const response = await axios.get(`${API}/analysis/${id}`, { timeout: 30000 });
        const data = response.data;

        if (data.status === "error") {
          throw new Error(data.error || "This analysis could not be completed");
        }

        if (data.status === "processing") {
          if (Date.now() - started > maxWaitMs) {
            throw new Error("Analysis is taking longer than expected. Please try again.");
          }
          await new Promise((r) => setTimeout(r, 3000));
          continue;
        }

        // complete
        if (data.video_filename) {
          data.videoUrl = `${BACKEND_URL}/api/uploads/${data.video_filename}`;
        }
        setAnalysisData(data);
        setProgress(100);
        setTimeout(() => setView("dashboard"), 300);
        return;
      }
    } catch (err) {
      console.error("Failed to load shared result:", err);
      setError(err.message || "This analysis link is invalid or has expired");
      setView("upload");
      navigate("/");
    }
  };

  const handleFileUpload = useCallback(async (file) => {
    setView("loading");
    setProgress(0);
    setError(null);

    // Create a local URL for instant video playback
    const videoUrl = URL.createObjectURL(file);

    const formData = new FormData();
    formData.append("video", file);

    // Smooth progress animation while we upload + poll
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? 95 : prev + Math.random() * 4));
    }, 600);

    const cleanup = () => {
      clearInterval(progressInterval);
    };

    try {
      // 1) Kick off the analysis (returns quickly with an id)
      const startRes = await axios.post(`${API}/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // allow large uploads
      });

      if (!startRes.data.success || !startRes.data.analysis_id) {
        throw new Error(startRes.data.error || "Could not start analysis");
      }

      const analysisId = startRes.data.analysis_id;

      // 2) Poll for completion
      const started = Date.now();
      const maxWaitMs = 5 * 60 * 1000; // 5 minutes

      while (true) {
        await new Promise((r) => setTimeout(r, 3000));

        if (Date.now() - started > maxWaitMs) {
          throw new Error("Analysis is taking longer than expected. Please try again.");
        }

        let statusRes;
        try {
          statusRes = await axios.get(`${API}/analysis/${analysisId}`, { timeout: 30000 });
        } catch (pollErr) {
          // Transient network blip while polling — keep trying until maxWait
          continue;
        }

        const result = statusRes.data;

        if (result.status === "complete") {
          cleanup();
          setProgress(100);
          const data = { ...result, videoUrl };
          setTimeout(() => {
            setAnalysisData(data);
            setView("dashboard");
            navigate(`/results/${analysisId}`);
          }, 500);
          return;
        }

        if (result.status === "error") {
          throw new Error(result.error || "Analysis failed");
        }
        // else still "processing" — keep polling
      }
    } catch (err) {
      cleanup();
      URL.revokeObjectURL(videoUrl);
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || err.message || "Upload failed");
      setView("upload");
    }
  }, [navigate]);

  const handleNewAnalysis = () => {
    setView("upload");
    setAnalysisData(null);
    setProgress(0);
    setError(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="app-container">
      {view === "upload" && (
        <UploadView onUpload={handleFileUpload} error={error} />
      )}
      {view === "loading" && <LoadingView progress={progress} />}
      {view === "dashboard" && analysisData && (
        <Dashboard data={analysisData} onNewAnalysis={handleNewAnalysis} />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AppContent />} />
        <Route path="/results/:resultId" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
